import { Button, Cell, Grid, Heading, Icon, Tag, Tooltip, VFlow } from 'bold-ui'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { ProcedimentoSelectField, ProcedimentoSelectModel } from 'components/form/field/select/ProcedimentoSelectField'
import { Form, FormRenderProps } from 'components/form/final-form/Form'
import { getFieldError } from 'components/form/final-form/util'
import { HLabel } from 'components/HLabel'
import { FormApi } from 'final-form'
import { GrupoProcedimentoEnum, TipoProcedimentoEnum } from 'graphql/types.generated'
import { useProcedimentoAutomatico } from 'hooks/procedimento-automatico/useProcedimentoAutomatico'
import React, { useEffect, useMemo, useState } from 'react'
import metaPath, { MetaPath } from 'util/metaPath/metaPath'
import { createValidator, maxLength } from 'util/validation'
import { getEditableItemRemoveTooltipText } from 'view/atendimentos/atendimento-individual/atendimento-observacao/util-atendObservacao'
import { SoapEditableItem } from 'view/atendimentos/atendimento-individual/model'
import { ProcedimentoModel } from 'view/atendimentos/model-procedimento'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'
import { getSexoCidadao } from 'view/atendimentos/utils/util-sexoCidadao'

import { EditableList, EditableRow, useEditableListField } from '../../../components/EditableList'
import { Observacao } from '../../../components/Observacao'
import { ObservacaoFormGrid } from '../../../components/ObservacaoFormGrid'
import { showCodigoProcedimento } from '../../objetivo/resultados-exames/util-resultadosExames'
import { getCbosIdsProcedimentos, hasMudancaProcedimentosAutomaticos } from '../utils/util-plano'
import { ProcedimentosPlanoModel } from './IntervencoesProcedimentosView'

export interface ProcedimentoPlanoModel extends ProcedimentoModel, SoapEditableItem {
  observacao?: string
}

const meta = metaPath<ProcedimentoPlanoModel>()

const GRUPOS_PROCEDIMENTO = [
  GrupoProcedimentoEnum.ACOES_PROMOCAO_PREVENCAO_SAUDE,
  GrupoProcedimentoEnum.FINALIDADE_DIAGNOSTICA,
  GrupoProcedimentoEnum.CLINICOS,
  GrupoProcedimentoEnum.CIRURGICO,
  GrupoProcedimentoEnum.ACOES_COMPLEMENTARES_ATENCAO_SAUDE,
]

export interface ProcedimentoPlanoProps {
  name: MetaPath<ProcedimentosPlanoModel>
  cidadao?: CidadaoAtendimento
  filtrarProcedimentosOdonto?: boolean
  required: boolean
  dataAtendimento: LocalDate
  onAutomaticChange?(): void
}

const validator = createValidator<ProcedimentoPlanoModel>({
  observacao: [maxLength(200)],
})

const path = metaPath<ProcedimentoPlanoModel>()

export function ProcedimentoPlanoField(props: ProcedimentoPlanoProps) {
  const { name, cidadao, filtrarProcedimentosOdonto, required, dataAtendimento, onAutomaticChange } = props
  const { acesso } = useAcessoLotacaoOrEstagio()

  const {
    handleRowChanged,
    handleSubmit,
    meta,
    input: { value },
  } = useEditableListField({ name: name.procedimentos })

  const { removeProcedimentoAutomatico } = useProcedimentoAutomatico({
    nameProcedimentos: name.procedimentos,
    nameCodigosProcedimentosAutomaticosExcluidos: name.codigosProcedimentosAutomaticosExcluidos,
  })

  const listaProcedimentos = value || []
  const [listaProcedimentosAnterior, setListaProcedimentosAnterior] = useState(listaProcedimentos)

  const hasMudancaProcedAutomaticos = useMemo(
    () => hasMudancaProcedimentosAutomaticos(listaProcedimentos, listaProcedimentosAnterior),
    [listaProcedimentos, listaProcedimentosAnterior]
  )

  useEffect(() => {
    if (hasMudancaProcedAutomaticos) {
      onAutomaticChange()
      setListaProcedimentosAnterior(listaProcedimentos)
    }
  }, [listaProcedimentos, hasMudancaProcedAutomaticos, onAutomaticChange])

  const procedimentoIsEqual = (procedimento: ProcedimentoSelectModel) => {
    return (value || []).find((o2) => procedimento.id === o2.procedimento.id)
  }

  const renderForm = (formProps: FormRenderProps<ProcedimentoPlanoModel>) => {
    const handleChange = (newValue: ProcedimentoSelectModel) => {
      newValue && !procedimentoIsEqual(newValue) && formProps.form.submit()
    }

    return (
      <ProcedimentoSelectField
        label='SIGTAP'
        onChange={handleChange}
        name={path.procedimento}
        itemIsEqual={procedimentoIsEqual}
        filtroPadrao
        tipoProcedimento={TipoProcedimentoEnum.CLINICO}
        gruposProcedimento={GRUPOS_PROCEDIMENTO}
        sexo={getSexoCidadao(cidadao)}
        dataNascimento={cidadao?.dataNascimento}
        dataAtendimento={dataAtendimento}
        cboIds={getCbosIdsProcedimentos(acesso)}
        filtrarProcedimentosOdonto={filtrarProcedimentosOdonto}
        loadItemsOnOpen={false}
        error={getFieldError(meta)}
        required={required}
      />
    )
  }

  return (
    <Grid>
      <Cell size={8}>
        <Form<ProcedimentoPlanoModel> render={renderForm} validate={validator} onSubmit={handleSubmit} />
      </Cell>
      {value?.length > 0 && (
        <Cell size={12}>
          <EditableList>
            {value.map((item) => (
              <ProcedimentoRow
                key={item._id}
                model={item}
                onChange={handleRowChanged}
                onRemove={removeProcedimentoAutomatico}
              />
            ))}
          </EditableList>
        </Cell>
      )}
    </Grid>
  )
}

interface ProcedimentoRowProps {
  model: ProcedimentoPlanoModel

  onChange(values: ProcedimentoPlanoModel): void

  onRemove(itemToRemove: ProcedimentoPlanoModel): void
}

function ProcedimentoRow(props: ProcedimentoRowProps) {
  const { model, onChange, onRemove } = props
  const [editing, setEditing] = useState(false)

  const { isEditable, tooltipText: removeTooltipText } = getEditableItemRemoveTooltipText(model)

  const editTooltipText = model.observacao ? 'Editar observação' : 'Adicionar observação'

  const toggleEditing = () => setEditing((oldState) => !oldState)

  const handleRemove = () => onRemove(model)

  const handleSubmit = (value: ProcedimentoPlanoModel, formApi: FormApi) => {
    onChange(value)
    setTimeout(formApi.reset)
    toggleEditing()
  }

  const renderForm = (formRenderProps: FormRenderProps<ProcedimentoPlanoModel>) => (
    <VFlow vSpacing={0.5} style={{ padding: '1rem 0' }}>
      <Heading level={4}>Adicionar observação</Heading>
      <HLabel title='SIGTAP:'>{model.procedimento.descricao}</HLabel>
      <ObservacaoFormGrid name={meta.observacao} onCancel={toggleEditing} onSubmit={formRenderProps.handleSubmit} />
    </VFlow>
  )

  return (
    <EditableRow editing={editing}>
      {!editing && (
        <>
          <Grid style={{ margin: 0 }}>
            <Cell size={9} alignSelf='center'>
              {`${model.procedimento.descricao} - ${showCodigoProcedimento(model.procedimento.codigo)}`}
              {model.automatico && <Tag style={{ marginLeft: '0.5rem' }}>Adicionado automaticamente</Tag>}
            </Cell>
            <Cell size={3} alignSelf='center' style={{ padding: '0.25rem 0', textAlign: 'right' }}>
              <Tooltip text={editTooltipText}>
                <Button size='small' skin='ghost' onClick={toggleEditing}>
                  <Icon icon='chatOutline' />
                </Button>
              </Tooltip>
              <Tooltip text={removeTooltipText}>
                <Button size='small' skin='ghost' onClick={handleRemove} disabled={isEditable}>
                  <Icon icon='trashOutline' />
                </Button>
              </Tooltip>
            </Cell>
          </Grid>
          <Observacao observacao={model.observacao} />
        </>
      )}
      {editing && (
        <Form<ProcedimentoPlanoModel>
          render={renderForm}
          initialValues={model}
          onSubmit={handleSubmit}
          validate={validator}
          initialValuesEqual={(oldInitial, newInitial) => oldInitial._id === newInitial._id}
        />
      )}
    </EditableRow>
  )
}
